import { UserDTO } from '../user-management/auth.type';
import { Division, LineItemLabel, LineItemUnit, LineItemWithDivision } from '../glossary/glossary.type';
import { ProjectDraw, ProjectInfoDTO } from '../project/project.type';
import { Comp } from '../comp/comp.type';
import * as yup from 'yup';
import { GridRowId } from '@mui/x-data-grid';
import { NoteDTO } from 'common/utils/util.type';

export interface CA_REPORT {
  id: string;
  generalInfo: ReportGeneralInfo;
  status: string;
  dynamicData: string;
  contractorTotal?: number;
  cdccTotal?: number;
  cdccCostPerSF?: number;
  budgetDated?: string | null;
  costDataFrom?: string;
  deviationPercentage?: number;
  lineItems: ReportLineItem[];
  selectedDraws: ProjectDraw[];
  selectedComps: Comp[];
  project: ProjectInfoDTO;
  invoices?: CAReportInvoiceDTO[];
  errorMessage?: string;
  createdBy?: UserDTO;
  updatedBy?: UserDTO;
  createdOn?: string;
  updatedOn?: string;
}

export interface ProjectCostPerUnit {
  buildingGSF: number;
  buildingNRSF: number;
  building: number;
  story: number;
  unit: number;
  acre: number;
}

export interface CostPerUnit {
  hardCostPerBuildingGSF: number;
  projectCostPerBuildingNRSF: number;
  projectCostPerUnit: number;
}

export interface CostComparison {
  contractorHardCostTotal: number;
  cdccHardCostTotal: number;
  cdccCostPerGSF: number;
  deviationPercentage: number;
}

export interface ReportLineItem {
  id: string;
  lineItem: LineItemWithDivision;
  division: Division | null;
  quantity: number;
  totalBudget: number;
  unit: LineItemUnit;
  label: LineItemLabel;
  cdccCompPerUnit: number;
  isEdited?: boolean;
  isDeleted?: boolean;
  updateDivisionGlobally?: boolean;
  note?: NoteDTO;
  notation?: string;
  type?: string;
}
export interface CreateLineItemDTO {
  reportId: string;
  description: string;
  totalBudget: number;
  quantity: number;
  unit: LineItemUnit;
  divisionId: string | null;
  cdccCompPerUnit: number;
  notation?: string;
  type?: string;
}

export interface ReportGeneralInfo {
  reportName?: string;
  grossBuildingSF?: number;
  netRentableSF?: number;
  siteAcreage?: number;
  unitCount?: number;
  buildingCount?: number;
  storyCount?: number;
  // following fields are to be used on frontend only
  elevatorCount?: number;
  costThreshold?: number;
  isEdited?: boolean;
}

export interface ReportRequest {
  drawIds: string[];
  generalInfoDto: ReportGeneralInfo;
}

export interface UpdateReportRequest {
  generalInfoDto: ReportGeneralInfo;
  status?: string;
  budgetDated?: string | null;
  costDataFrom?: string;
  dynamicData?: string;
  contractorTotal?: number;
  cdccTotal?: number;
  cdccCostPerSF?: number;
  deviationPercentage?: number;
}

export interface UpdateReportLineItemRequest {
  lineItemId?: string;
  quantity?: number;
  unit?: LineItemUnit;
  cdccCompPerUnit?: number;
  divisionId: string | null;
  notation?: string;
}
export interface BulkUpdateReportLineItemRequest {
  updatedReportLineItems: UpdateReportLineItemRequest[];
}

export type ReportInfoListResponse = {
  reportsList: ReportListInfo[];
  total: number;
};
export interface ReportListInfo {
  id: string;
  reportName: string;
  status: string;
  createdOn: string;
  upatedOn: string;
  createdBy?: UserDTO;
  updatedBy?: UserDTO;
  project: ProjectInfoDTO;
  comparablesCount: number;
  contractorTotal?: number;
  cdccTotal?: number;
  cdccCostPerSF?: number;
  deviationPercentage?: number;
  budgetDated?: string | null;
  costDataFrom?: string;
}
export type RecentlyUploadedReportsDTO = {
  id: string;
  reportName: string;
  createdOn: string;
  updatedOn: string;
  createdBy?: UserDTO;
};
// export interface DivisionProjectsResponseDTO {
//   isSuccess: boolean;
//   data: { [divisionId: string]: ProjectInfoDTO[] };
//   errorMessage?: string;
// }
export type UpdateDivisionProjectDrawsDTO = {
  divisionId: string;
  newProjectDrawIds: string[];
};

export interface ReportLineItemBulkEditFormValues {
  division: Division | null;
  updateDivisionGlobally: boolean;
  label: LineItemLabel | null;
  changeDivision: boolean;
  changeLabel: boolean;
}

export enum ReportLineItemNotations {
  LOWER_THAN_NORMAL = 'Lower than Normal',
  HIGHER_THAN_NORMAL = 'Higher than Normal',
  ACCEPTABLE = 'Acceptable',
}

export type ReportLineItemNotation =
  | ReportLineItemNotations.LOWER_THAN_NORMAL
  | ReportLineItemNotations.HIGHER_THAN_NORMAL
  | ReportLineItemNotations.ACCEPTABLE
  | null;

export type generateReportCompsDTO = {
  caReportId: string;
  projectIds: GridRowId[];
  divisionIds?: string[]; // New optional field
};

// export interface CAReportDivisionProjectDraws {

export type CompsDivisionAveragesDTO = {
  compId: string;
  budgetPerUnit: number;
  isIncluded: boolean;
};

export type LabelType = 'Allowances' | 'Mobilizations';

export const labelColors: Record<LabelType, string> = {
  Allowances: '#0283a0',
  Mobilizations: '#D24C58',
};

export enum ReportLineItemTypes {
  NORMAL = 'Normal',
  DivisionTotal = 'DivisionTotal',
  CompPricing = 'CompPricing',
  Adjustment = 'Adjustment',
  HardCostSubTotal = 'HardCostSubTotal',
  TotalProjectedHardCosts = 'TotalProjectedHardCosts',
  TotalSoftCosts = 'TotalSoftCosts',
  TotalProjectedCosts = 'TotalProjectedCosts',
}
export const CAReportLineItemValidationSchema = yup.object({
  originalBudget: yup.number().required('Required').typeError('Must be a number'),
  totalBudget: yup.number().required('Required').typeError('Must be a number'),
  quantity: yup.number().min(0, 'Must not be negative').required('Required').typeError('Must be a number'),
  cdccCompPerUnit: yup.number().min(0, 'Must not be negative').required('Required').typeError('Must be a number'),
});

export enum ReportStatusEnum {
  InProgress = 'InProgress',
  Completed = 'Completed',
  Updated = 'Updated',
}

// Invoice related types for Cost Analysis Reports
export interface CAReportInvoiceDTO {
  id: string;
  caReportId: string;
  invoice: {
    invoiceDate: any;
    id: string;
    number: string;
    invoiceAmount: number;
    paidAmount: number;
  };
  recepient?: string;
  recepientAddress?: {
    street?: string;
    city?: string;
    state?: string;
    postalCode?: string;
  };
  projectName?: string;
  projectAddress?: {
    street?: string;
    city?: string;
    state?: string;
    postalCode?: string;
  };
  service?: string;
  createdOn?: string;
  updatedOn?: string;
}

export interface CreateCAReportInvoiceDTO {
  caReportId: string;
  invoice: {
    number: string;
    invoiceDate: any;
    invoiceAmount: number;
    paidAmount: number;
  };
  recepient?: string;
  recepientAddress?: {
    street?: string;
    city?: string;
    state?: string;
    postalCode?: string;
  };
  projectName?: string;
  projectAddress?: {
    street?: string;
    city?: string;
    state?: string;
    postalCode?: string;
  };
  service?: string;
}

export interface UpdateCAReportInvoiceDTO {
  invoice: {
    number: string;
    invoiceAmount: number;
    paidAmount: number;
  };
  invoiceDate?: string;
  recepient?: string;
  recepientAddress?: {
    street?: string;
    city?: string;
    state?: string;
    postalCode?: string;
  };
  projectName?: string;
  projectAddress?: {
    street?: string;
    city?: string;
    state?: string;
    postalCode?: string;
  };
  service?: string;
}
